'use client';

import { c as _c } from "react/compiler-runtime";
import { type Factiii } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { isUrl, mapLinkHref } from '@shared/all/validators';
import { tagValues } from '@shared/utils/helpers/tagValues';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import { Archive, BookOpen, ChevronDown, Hash, Link as LinkIcon, type LucideIcon, MapPin, Users } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { client } from '../../utils/trpc';
import { MapOverlayContext } from '../cesium/MapOverlayContext';
import Avatar from '../common/Avatar';
import FactiiiStrip, { FACTIII_STRIP_OVERFLOW_WIDTH } from '../factiiis/FactiiiStrip';
import LinkPreview from '../UI/LinkPreview';
import FactiiiAvatarHoverCard from './FactiiiAvatarHoverCard';
import FactiiiInfoChip from './FactiiiInfoChip';
import PostFactiiiButton from './PostFactiiiButton';
import ReferencedPostsModal from './ReferencedPostsModal';
import { RenderRawScrapeModal } from './RenderRawScrapeModal';
import TagValuesPanel from './TagValuesPanel';
type PanelKey = 'values' | 'pins' | 'people' | 'links' | 'retired' | 'refs';
const POPOVER_MIN_HEIGHT = 220;

// Collapsed-row sizing — a single source of truth so the chip-fit measurement
// below can't drift from what actually renders. The avatars fit themselves
// (FactiiiStrip); this row only tells the strip how big they are and how tall
// it may be.
const FACTIII_AVATAR_SIZE = 22; // px — the <Avatar> size in the collapsed row
// One row of avatars plus the 4px above and below that keeps the header's
// line height where it was.
const FACTIII_ROW_HEIGHT = FACTIII_AVATAR_SIZE + 8;
const CLUSTER_GAP = 6; // px — the gap between the chips / toggle / avatar groups
// View-more caret reserve — held back so the fit math never paints chips into
// the toggle slot (matches mobile). Used until the real toggle is mounted.
const TOGGLE_RESERVE = 28;
// Leading "F" button plus the gap after it, for the row's minimum width.
const ADD_BUTTON_RESERVE = 46;
// Estimated width of one info chip for fit math: fixed chrome (border + pad +
// icon + inner gap) + per digit + the gap after the chip. Conservative so a
// tight row drops chips rather than clipping them.
const CHIP_BASE_W = 38;
const CHIP_DIGIT_W = 8;
const CHIP_GAP = 8; // matches the gap-2 between chips

// Position/width of the full-width expanded dropdown, measured from the
// surrounding post card so the panel spans the whole post.
type DropdownRect = {
  top: number;
  left: number;
  width: number;
};
type CountState = {
  factiiis: Factiii[];
  expanded: boolean;
  dropdownRect: DropdownRect | null;
  popover: {
    key: PanelKey;
    rect: DOMRect;
  } | null;
  focusedPin: {
    latitude: number;
    longitude: number;
  } | null;
  hover: {
    factiiiId: number;
    rect: DOMRect;
    visible: boolean;
  } | null;
  // scrapeId whose raw scrape is shown in the Raw Data modal; null when closed.
  rawScrapeId: number | null;
  // Whether the referenced-posts modal (real post previews) is open.
  refsModalOpen: boolean;
};
type CountAction = {
  type: 'SET_FACTIIIS';
  factiiis: Factiii[];
} | {
  type: 'TOGGLE_EXPANDED';
} | {
  type: 'SET_EXPANDED';
  expanded: boolean;
} | {
  type: 'SET_DROPDOWN_RECT';
  rect: DropdownRect;
} | {
  type: 'SET_POPOVER';
  popover: CountState['popover'];
} | {
  type: 'SET_FOCUSED_PIN';
  pin: CountState['focusedPin'];
} | {
  type: 'SET_HOVER';
  hover: CountState['hover'];
} | {
  type: 'SET_RAW_SCRAPE';
  rawScrapeId: number | null;
} | {
  type: 'SET_REFS_MODAL';
  open: boolean;
};
const countReducer = (state: CountState, action: CountAction): CountState => {
  switch (action.type) {
    case 'SET_FACTIIIS':
      return {
        ...state,
        factiiis: action.factiiis
      };
    case 'TOGGLE_EXPANDED':
      return {
        ...state,
        expanded: !state.expanded
      };
    case 'SET_EXPANDED':
      return {
        ...state,
        expanded: action.expanded
      };
    case 'SET_DROPDOWN_RECT':
      return {
        ...state,
        dropdownRect: action.rect
      };
    case 'SET_POPOVER':
      return {
        ...state,
        popover: action.popover
      };
    case 'SET_FOCUSED_PIN':
      return {
        ...state,
        focusedPin: action.pin
      };
    case 'SET_HOVER':
      return {
        ...state,
        hover: action.hover
      };
    case 'SET_RAW_SCRAPE':
      return {
        ...state,
        rawScrapeId: action.rawScrapeId
      };
    case 'SET_REFS_MODAL':
      return {
        ...state,
        refsModalOpen: action.open
      };
  }
};
const MapView = dynamic(() => import('../cesium/MapView'), {
  ssr: false
});
type Props = {
  post: Post;
  ignorePostFactiiiIds: number[];
  // A factiiiId to omit from the breakdown — used on board cards so a card in
  // the "Done" list doesn't redundantly show the "Done" factiii it lives under.
  ignoreFactiiiId?: number;
  // Factiii flags + setters — when provided, the leading "F" add button renders
  // and opens the attach-factiii modal (which writes the flags back up). Absent
  // in preview/menu-less contexts, where only the breakdown shows.
  nsfw?: boolean;
  politics?: boolean;
  troll?: boolean;
  setNsfw?: (arg0: boolean) => void;
  setPolitics?: (arg0: boolean) => void;
  setTroll?: (arg0: boolean) => void;
  uniqueId?: string;
  minified?: boolean;
};
export default function PostFactiiisCount(props: Props) {
  const {
    getFactiiiIds
  } = useFactiiis(client);
  const router = useRouter();
  const searchParams = useSearchParams();
  // True when an ancestor <MapView> is in 'full' mode (provided by
  // MapOverlayContext). The embedded mini-map is redundant — the big map
  // already shows the same pins — and clicking a location should fly the
  // big map's camera, not the embed's.
  const isInBigMap = useContext(MapOverlayContext);
  const [state, dispatch] = useReducer(countReducer, {
    factiiis: [],
    expanded: false,
    dropdownRect: null,
    popover: null,
    focusedPin: null,
    hover: null,
    rawScrapeId: null,
    refsModalOpen: false
  });
  const {
    factiiis,
    expanded,
    dropdownRect,
    popover,
    focusedPin,
    hover,
    rawScrapeId,
    refsModalOpen
  } = state;
  // The collapsed trigger bar; the expanded detail opens as a portal dropdown
  // anchored to the post card so it can span the full post width.
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pinsButtonRef = useRef<HTMLButtonElement>(null);
  const hoverUnmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The view-more caret — measured so the fit calc reserves its real width
  // instead of a fixed guess.
  const toggleRef = useRef<HTMLButtonElement>(null);
  // How many always-show chips (Links/Refs/Locations/Users) fit before the
  // rest spill into view more. Starts at 0 so the first paint is minimal — the
  // measurement below then expands the chips to the real available width
  // (avoids an initial overflow flash before measuring).
  const [chipsThatFit, setChipsThatFit] = useState(0);
  const showHover = (factiiiId: number, rect: DOMRect) => {
    if (hoverUnmountTimer.current) {
      clearTimeout(hoverUnmountTimer.current);
      hoverUnmountTimer.current = null;
    }
    dispatch({
      type: 'SET_HOVER',
      hover: {
        factiiiId,
        rect,
        visible: true
      }
    });
  };
  const hideHover = (factiiiId_0: number) => {
    if (hover?.factiiiId === factiiiId_0) {
      dispatch({
        type: 'SET_HOVER',
        hover: {
          ...hover,
          visible: false
        }
      });
    }
    if (hoverUnmountTimer.current) clearTimeout(hoverUnmountTimer.current);
    hoverUnmountTimer.current = setTimeout(() => {
      dispatch({
        type: 'SET_HOVER',
        hover: null
      });
      hoverUnmountTimer.current = null;
    }, 180);
  };
  useEffect(() => {
    return () => {
      if (hoverUnmountTimer.current) clearTimeout(hoverUnmountTimer.current);
    };
  }, []);

  // Measure the dropdown's position/width from the surrounding post card so
  // the expanded panel spans the whole post. Falls back to the trigger's own
  // box when no post-card ancestor exists (e.g. map overlay usage).
  const measureDropdown = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const card = trigger.closest<HTMLElement>('[data-post-card="true"]');
    const anchor = card ?? trigger;
    const anchorRect = anchor.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    dispatch({
      type: 'SET_DROPDOWN_RECT',
      rect: {
        top: triggerRect.bottom + 8,
        left: anchorRect.left,
        width: anchorRect.width
      }
    });
  };

  // While open, keep the dropdown glued to the trigger on scroll/resize and
  // close it on Escape or a pointer-down outside the dropdown/trigger.
  useEffect(() => {
    if (!expanded) return;
    const reposition = () => measureDropdown();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch({
        type: 'SET_EXPANDED',
        expanded: false
      });
    };
    const onPointerDown = (e_0: PointerEvent) => {
      const target = e_0.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-post-factiiis-dropdown="true"]')) return;
      if (target.closest('[data-post-factiiis-toggle="true"]')) return;
      // Sub-panel popovers + hover cards portal separately — keep them open.
      if (target.closest('[data-post-factiiis-popover="true"]')) return;
      dispatch({
        type: 'SET_EXPANDED',
        expanded: false
      });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [expanded]);
  const cancelPopoverClose = () => {
    if (popoverCloseTimer.current) {
      clearTimeout(popoverCloseTimer.current);
      popoverCloseTimer.current = null;
    }
  };
  const schedulePopoverClose = () => {
    cancelPopoverClose();
    popoverCloseTimer.current = setTimeout(() => {
      dispatch({
        type: 'SET_POPOVER',
        popover: null
      });
      popoverCloseTimer.current = null;
    }, 160);
  };
  const openPopover = (key: PanelKey, rect_0: DOMRect) => {
    cancelPopoverClose();
    dispatch({
      type: 'SET_POPOVER',
      popover: {
        key,
        rect: rect_0
      }
    });
  };
  const togglePopover = (key_0: PanelKey, rect_1: DOMRect) => {
    cancelPopoverClose();
    dispatch({
      type: 'SET_POPOVER',
      popover: popover?.key === key_0 ? null : {
        key: key_0,
        rect: rect_1
      }
    });
  };

  // Close popover on scroll, escape, or pointer-down outside of it.
  useEffect(() => {
    if (!popover) return;
    const close = () => dispatch({
      type: 'SET_POPOVER',
      popover: null
    });
    // Capture-phase scroll also fires for the popover's own list, so scrolling
    // a long panel (Values, Links) used to close it mid-scroll. Only a scroll
    // outside the popover dismisses it.
    const onScroll = (e_1: Event) => {
      if (e_1.target instanceof Element && e_1.target.closest('[data-post-factiiis-popover="true"]')) return;
      close();
    };
    const onKey_0 = (e_2: KeyboardEvent) => {
      if (e_2.key === 'Escape') close();
    };
    const onPointerDown_0 = (e_3: PointerEvent) => {
      const target_0 = e_3.target as HTMLElement | null;
      if (!target_0) return;
      if (target_0.closest('[data-post-factiiis-popover="true"]')) return;
      if (target_0.closest('[data-post-factiiis-trigger="true"]')) return;
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey_0);
    window.addEventListener('pointerdown', onPointerDown_0);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey_0);
      window.removeEventListener('pointerdown', onPointerDown_0);
    };
  }, [popover]);
  useEffect(() => {
    return () => {
      if (popoverCloseTimer.current) clearTimeout(popoverCloseTimer.current);
    };
  }, []);
  useEffect(() => {
    const fetchFactiiis = async () => {
      const result = await getFactiiiIds(props.post.postFactiiisCount.map(pfc => pfc.factiiiId));
      dispatch({
        type: 'SET_FACTIIIS',
        factiiis: result
      });
    };
    fetchFactiiis();
  }, [props.post.postFactiiisCount]);
  const pfcs = props.post.postFactiiisCount.filter(pfc_0 => pfc_0.factiiiId !== props.ignoreFactiiiId);

  // Aggregate everything across all factiiis + entries on this post.
  // Track which factiii each entry came from so list pins can render with
  // that factiii's color/avatar.
  const entries = pfcs.flatMap(pfc_1 => pfc_1.postFactiiis.map(pf => ({
    ...pf,
    _factiiiId: pfc_1.factiiiId
  })));
  const pins = entries.flatMap(pf_0 => pf_0.spaceTimes.filter((st): st is typeof st & {
    latitude: number;
    longitude: number;
  } => st.latitude != null && st.longitude != null).map(st_0 => ({
    postId: props.post.id,
    latitude: st_0.latitude,
    longitude: st_0.longitude,
    display: st_0.display,
    factiiiId: pf_0._factiiiId
  })));

  // Dedupe pins for the dropdown list (keep one per unique coord+display).
  // First-seen wins, so the first factiii to tag a location colors its pin.
  const uniquePins = Array.from(new Map(pins.map(p => [`${p.latitude},${p.longitude},${p.display ?? ''}`, p])).values());
  const uniqueTaggers = Array.from(new Map(entries.filter(pf_1 => !!pf_1.user).map(pf_2 => [pf_2.user.id, pf_2.user])).values());

  // Keep the factiii each link came from so the panel can show its avatar.
  // Dedupe by URL; first-seen factiii wins (matches the pins behavior).
  const uniqueLinks = Array.from(new Map(entries.filter((pf_3): pf_3 is typeof pf_3 & {
    data: string;
  } => !!pf_3.data && isUrl(pf_3.data)).map(pf_4 => [pf_4.data, {
    url: pf_4.data,
    factiiiId: pf_4._factiiiId
  }])).values());

  // Lookup keyed by factiiiId so PinsPanel rows can render the right
  // factiii avatar inline. The map starts empty; rows render the indigo
  // default + Logo fallback until the fetch resolves.
  const factiiisById = new Map(factiiis.map(f => [f.id, f]));
  const refCount = entries.reduce((n, pf_5) => n + (pf_5.references?.length ?? 0), 0);
  const sourceCount = entries.reduce((n_0, pf_6) => n_0 + (pf_6.factiiiReferences?.length ?? 0), 0);

  // Unique referenced posts (post refs) + source factiiis (factiii refs) for
  // the citations popover. Sources carry their own factiii object, so no extra
  // fetch is needed; dedupe by factiii id, first-seen wins.
  // Each referenced post carries the factiii it was referenced under (the
  // owning entry's factiii), so the panel can show that factiii's avatar + name
  // just like the source rows do.
  const uniqueRefPosts = Array.from(new Map(entries.flatMap(pf_7 => (pf_7.references ?? []).map(r => [r.uuid, {
    uuid: r.uuid,
    factiiiId: pf_7._factiiiId
  }] as const))).values());
  const uniqueSources = Array.from(new Map(entries.flatMap(pf_8 => (pf_8.factiiiReferences ?? []).map(r_0 => [r_0.factiii.id, r_0.factiii] as const))).values());

  // What each tag carries that no other chip shows: a number, text, a date.
  const values = pfcs.flatMap(pfc_2 => tagValues(pfc_2.factiiiId, pfc_2.postFactiiis));
  const resolvedFactiiis = pfcs.map(pfc_3 => ({
    pfc: pfc_3,
    factiii: factiiis.find(f_0 => f_0.id === pfc_3.factiiiId)
  })).filter((x): x is {
    pfc: (typeof pfcs)[number];
    factiii: Factiii;
  } => !!x.factiii);

  // Retired factiiis move to their own group below; the top row only carries
  // active ones.
  const activeFactiiis = resolvedFactiiis.filter(r_1 => r_1.factiii.status !== 'RETIRED');
  const retiredFactiiis = resolvedFactiiis.filter(r_2 => r_2.factiii.status === 'RETIRED');
  const hasActive = activeFactiiis.length > 0;

  // The leading "F" add button shows whenever we have the flag setters (i.e.
  // not a preview); the breakdown shows only when factiiis exist. Render
  // nothing when neither applies.
  const canAddFactiii = !!props.setNsfw && !!props.setPolitics && !!props.setTroll;
  const hasFactiiis = pfcs.length > 0;

  // Always-show chip descriptors (Values → Links → Citations → Locations →
  // Users). Archive is never in this list — it always lives in view more.
  // Counts drive the width estimate used by the fit math below.
  type ChipDesc = {
    key: PanelKey;
    icon: LucideIcon;
    count: number;
    buttonRef?: typeof pinsButtonRef;
  };
  const chipDescriptors: ChipDesc[] = [values.length > 0 && {
    key: 'values' as const,
    icon: Hash,
    count: values.length
  }, uniqueLinks.length > 0 && {
    key: 'links' as const,
    icon: LinkIcon,
    count: uniqueLinks.length
  }, refCount + sourceCount > 0 && {
    key: 'refs' as const,
    icon: BookOpen,
    count: refCount + sourceCount
  }, uniquePins.length > 0 && {
    key: 'pins' as const,
    icon: MapPin,
    count: uniquePins.length,
    buttonRef: pinsButtonRef
  }, uniqueTaggers.length > 0 && {
    key: 'people' as const,
    icon: Users,
    count: uniqueTaggers.length
  }].filter(Boolean) as ChipDesc[];
  const hasArchive = retiredFactiiis.length > 0;
  const showMap = pins.length > 0 && !isInBigMap;
  const chipWidth = (count: number) => CHIP_BASE_W + String(count).length * CHIP_DIGIT_W + CHIP_GAP;

  // Single-row fit (matches mobile): chips are the priority. Fit as many
  // always-show chips as the width allows; any that don't fit drop into view
  // more. The factiii strip takes whatever width is left and fits its own
  // avatars, so all this holds back for it is the width of its "+N" badge.
  useEffect(() => {
    const trigger_0 = triggerRef.current;
    if (!trigger_0) return;
    const fitInto = (available: number) => {
      let used = 0;
      let fitChips = 0;
      for (const c of chipDescriptors) {
        const w = chipWidth(c.count);
        if (used + w <= available) {
          used += w;
          fitChips++;
        } else break;
      }
      return fitChips;
    };
    const update = () => {
      const width = trigger_0.getBoundingClientRect().width;
      if (width <= 0) return;
      const stripReserve = hasActive ? FACTIII_STRIP_OVERFLOW_WIDTH + CLUSTER_GAP : 0;
      // Prefer the real toggle width when mounted.
      const toggleEl = toggleRef.current;
      const toggleReserve = toggleEl ? toggleEl.getBoundingClientRect().width + CLUSTER_GAP : TOGGLE_RESERVE;

      // Pass 1: no caret unless Archive or the map already call for one.
      const alwaysToggle = hasArchive || showMap;
      let fitChips_0 = fitInto(width - stripReserve - (alwaysToggle ? toggleReserve : 0));
      if (!alwaysToggle && fitChips_0 < chipDescriptors.length) {
        // Pass 2: a chip was dropped, so the caret appears — reserve it so the
        // chips never paint into it.
        fitChips_0 = fitInto(width - stripReserve - toggleReserve);
      }
      setChipsThatFit(fitChips_0);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(trigger_0);
    if (toggleRef.current) ro.observe(toggleRef.current);
    return () => ro.disconnect();
    // Descriptors rebuild each render; key off the counts that define them.
  }, [hasActive, chipDescriptors.length, values.length, uniqueLinks.length, refCount, sourceCount, uniquePins.length, uniqueTaggers.length, hasArchive, showMap]);
  if (!hasFactiiis && !canAddFactiii) return null;

  // The trigger bar shows the chips that fit and the factiii strip; overflow
  // chips, Archive, and the map live in the expanded dropdown. Factiiis that
  // do not fit are the strip's own "+N", which opens every factiii in a modal.
  const inlineChipDescs = chipDescriptors.slice(0, chipsThatFit);
  const droppedChipDescs = chipDescriptors.slice(chipsThatFit);
  // View more only when it would hold something: width-dropped chips, Archive,
  // or locations (map).
  const hasDropdownContent = droppedChipDescs.length > 0 || hasArchive || showMap;

  // The least the row can be and still show the strip's "+N" badge. The post
  // header wraps, so a row squeezed below this moves to its own line instead
  // of clipping the badge (or losing the strip altogether).
  const minRowWidth = hasActive ? (canAddFactiii ? ADD_BUTTON_RESERVE : 0) + (hasDropdownContent ? TOGGLE_RESERVE + CLUSTER_GAP : 0) + FACTIII_STRIP_OVERFLOW_WIDTH : undefined;
  const renderFactiiiAvatar = (factiii: Factiii) => <Link key={factiii.id} href={`/f/${factiii.slug}`} onClick={e_4 => e_4.stopPropagation()} onMouseEnter={e_5 => showHover(factiii.id, e_5.currentTarget.getBoundingClientRect())} onMouseLeave={() => hideHover(factiii.id)} onFocus={e_6 => showHover(factiii.id, e_6.currentTarget.getBoundingClientRect())} onBlur={() => hideHover(factiii.id)} aria-label={`Open f/${factiii.slug}`} className="flex shrink-0 transition-transform hover:z-10 hover:scale-110">
      <Avatar factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={FACTIII_AVATAR_SIZE} isRetired={factiii.status === 'RETIRED'} />
    </Link>;
  const renderChip = (desc: ChipDesc) => <FactiiiInfoChip key={desc.key} icon={desc.icon} count={desc.count} buttonRef={desc.buttonRef} active={popover?.key === desc.key} onHoverOpen={rect_2 => openPopover(desc.key, rect_2)} onHoverClose={schedulePopoverClose} onToggleClick={rect_3 => togglePopover(desc.key, rect_3)} />;

  // Archive chip — only rendered inside the view-more dropdown.
  const retiredChip = hasArchive && <FactiiiInfoChip key="retired" icon={Archive} count={retiredFactiiis.length} active={popover?.key === 'retired'} onHoverOpen={rect_4 => openPopover('retired', rect_4)} onHoverClose={schedulePopoverClose} onToggleClick={rect_5 => togglePopover('retired', rect_5)} />;

  // Spillover chips in the dropdown: width-dropped always-show chips + Archive.
  const viewMoreChips = [...droppedChipDescs.map(renderChip), ...(retiredChip ? [retiredChip] : [])];
  return <>
      <div data-id="post-factiiis" className="flex min-w-0 flex-1 items-center gap-2.5" style={{
      minWidth: minRowWidth
    }}>
        {/* Leading "F" — always present (when we can add) so a post can gain its
            first factiii; opens the attach-factiii modal. */}
        {canAddFactiii && <PostFactiiiButton post={props.post} nsfw={Boolean(props.nsfw)} politics={Boolean(props.politics)} troll={props.troll} setNsfw={props.setNsfw} setPolitics={props.setPolitics} setTroll={props.setTroll} uniqueId={props.uniqueId} minified={props.minified} hideCount />}

        {/* Collapsed trigger bar — always a single line: the
            Links/Refs/Locations chips that fit, the view-more caret, then the
            factiii strip filling the rest. No surface, so it sits cleanly
            inline next to the post meta. The caret opens the full-width
            dropdown below. */}
        {hasFactiiis && <div ref={triggerRef} className="flex min-w-0 flex-1 items-center" style={{
        gap: CLUSTER_GAP
      }}>
            {/* Always-show chips that fit (Links/Refs/Locations/Users). Width
                spillover drops to view more — if not even one fits, all drop. */}
            {inlineChipDescs.length > 0 && <div className="flex shrink-0 items-center gap-2">
                {inlineChipDescs.map(renderChip)}
              </div>}

            {/* Caret — opens view more (dropped chips + Archive + map). It
                sits with the chips it extends, ahead of the strip, which
                grows to the end of the row. */}
            {hasDropdownContent && <button ref={toggleRef} type="button" data-post-factiiis-toggle="true" aria-expanded={expanded} aria-label="More info" onClick={e_7 => {
          e_7.stopPropagation();
          if (!expanded) measureDropdown();
          dispatch({
            type: 'TOGGLE_EXPANDED'
          });
        }} className="flex shrink-0 items-center gap-0.5 rounded-md px-0.5 py-1 text-textSecondary transition-colors hover:bg-primary/10 hover:text-textPrimary">
                <ChevronDown size={18} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>}

            {/* Factiii avatars — as many as fit, each its own link to the
                factiii; the strip's "+N" opens all of them in a modal. */}
            {hasActive && <FactiiiStrip factiiis={activeFactiiis.map(({
          factiii: factiii_0
        }) => factiii_0)} height={FACTIII_ROW_HEIGHT} avatarSize={FACTIII_AVATAR_SIZE} renderItem={renderFactiiiAvatar} title="Factiiis on this post" />}
          </div>}
      </div>

      {/* Expanded detail — full-width dropdown: dropped chips, Archive, and
          the map. */}
      {expanded && dropdownRect && typeof document !== 'undefined' && createPortal(<div data-post-factiiis-dropdown="true" style={{
      position: 'fixed',
      top: dropdownRect.top,
      left: dropdownRect.left,
      width: dropdownRect.width,
      // Cap to the space below the trigger and scroll when the chips +
      // map are taller than the viewport allows.
      maxHeight: `calc(100dvh - ${dropdownRect.top + 16}px)`,
      // z-popover (.specs/z-index.md); inline because the rest of
      // the box is positioned inline.
      zIndex: 400
    }} onClick={e_8 => e_8.stopPropagation()} className="overflow-y-auto overflow-x-hidden rounded-lg border border-ds-border-standard bg-background-primary p-3 shadow-xl">
            {/* Width-dropped always-show chips + Archive (always here). */}
            {viewMoreChips.length > 0 && <div className="mb-2 flex flex-wrap items-center gap-2">
                {viewMoreChips}
              </div>}

            {/* The map spans the full dropdown width. */}
            {showMap && <div className="mt-3 w-full" onClick={e_9 => e_9.stopPropagation()}>
                <MapView pins={pins} focusedPin={focusedPin} height={220} />
              </div>}
          </div>, document.body)}

      {popover && <PanelPopover anchorRect={popover.rect} onEnter={cancelPopoverClose} onLeave={schedulePopoverClose}>
          {popover.key === 'pins' && <PinsPanel postId={props.post.id} pins={uniquePins} factiiisById={factiiisById} focusedPin={focusedPin} onSelectPin={pin => {
        if (isInBigMap) {
          // Push lat/lng into the URL so the big /map's
          // CameraFlyTo (keyed by initialPosition) remounts and
          // flies to the new location.
          const params = new URLSearchParams(searchParams?.toString() || '');
          params.set('latitude', String(pin.latitude));
          params.set('longitude', String(pin.longitude));
          router.push(`/map?${params.toString()}`);
        } else {
          const isSame = focusedPin && focusedPin.latitude === pin.latitude && focusedPin.longitude === pin.longitude;
          const nextPin = isSame ? null : {
            latitude: pin.latitude,
            longitude: pin.longitude
          };
          dispatch({
            type: 'SET_FOCUSED_PIN',
            pin: nextPin
          });
          // Reveal the map so the focused pin is actually visible — the
          // map lives in the "More info" dropdown, so open it if it
          // isn't already showing.
          if (nextPin && !expanded) {
            measureDropdown();
            dispatch({
              type: 'SET_EXPANDED',
              expanded: true
            });
          }
        }
        dispatch({
          type: 'SET_POPOVER',
          popover: null
        });
      }} />}
          {popover.key === 'values' && <TagValuesPanel values={values} factiiisById={factiiisById} />}
          {popover.key === 'people' && <PeoplePanel taggers={uniqueTaggers} />}
          {popover.key === 'links' && <LinksPanel links={uniqueLinks} factiiisById={factiiisById} showHover={showHover} hideHover={hideHover} />}
          {popover.key === 'retired' && <RetiredPanel factiiis={retiredFactiiis.map(({
        factiii: factiii_1
      }) => factiii_1)} />}
          {popover.key === 'refs' && <ReferencesPanel refPosts={uniqueRefPosts} sources={uniqueSources} onViewPosts={() => {
        // Collapse the breakdown popover + expanded dropdown — both
        // portal above the modal (z popover/390 > z modal/200) and would
        // otherwise float over the references modal.
        dispatch({
          type: 'SET_POPOVER',
          popover: null
        });
        dispatch({
          type: 'SET_EXPANDED',
          expanded: false
        });
        dispatch({
          type: 'SET_REFS_MODAL',
          open: true
        });
      }} />}
        </PanelPopover>}

      {hover && (() => {
      const item = resolvedFactiiis.find(r_3 => r_3.factiii.id === hover.factiiiId);
      if (!item) return null;
      return <FactiiiAvatarHoverCard key={item.factiii.id} factiii={item.factiii} pfc={item.pfc} post={{
        id: props.post.id,
        isAuthor: props.post.isAuthor
      }} anchorRect={hover.rect} visible={hover.visible} onMouseEnter={() => showHover(item.factiii.id, hover.rect)} onMouseLeave={() => hideHover(item.factiii.id)} onViewRawData={scrapeId => {
        // Dismiss the hover card before opening the modal. The card
        // sits at z-400 but the modal is z-modal (200), so leaving it
        // mounted parks it above the modal backdrop where the next
        // click lands on the backdrop and instantly closes the modal.
        if (hoverUnmountTimer.current) {
          clearTimeout(hoverUnmountTimer.current);
          hoverUnmountTimer.current = null;
        }
        dispatch({
          type: 'SET_HOVER',
          hover: null
        });
        dispatch({
          type: 'SET_RAW_SCRAPE',
          rawScrapeId: scrapeId
        });
      }} />;
    })()}

      {rawScrapeId != null && <RenderRawScrapeModal scrapeId={rawScrapeId} closeHandler={() => dispatch({
      type: 'SET_RAW_SCRAPE',
      rawScrapeId: null
    })} />}

      {refsModalOpen && <ReferencedPostsModal postUuid={props.post.id} refCount={uniqueRefPosts.length} factiiiIds={[...new Set(uniqueRefPosts.map(r_4 => r_4.factiiiId))]} onClose={() => dispatch({
      type: 'SET_REFS_MODAL',
      open: false
    })} />}
    </>;
}
type PanelPopoverProps = {
  anchorRect: DOMRect;
  onEnter: () => void;
  onLeave: () => void;
  children: React.ReactNode;
};
const POPOVER_WIDTH = 320;
const POPOVER_MARGIN = 8;
const POPOVER_MAX_HEIGHT = 360;
function PanelPopover(t0) {
  const $ = _c(18);
  const {
    anchorRect,
    onEnter,
    onLeave,
    children
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!mounted || typeof window === "undefined") {
    return null;
  }
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const width = Math.min(POPOVER_WIDTH, viewportW - POPOVER_MARGIN * 2);
  const left = Math.min(Math.max(POPOVER_MARGIN, anchorRect.left + anchorRect.width / 2 - width / 2), viewportW - width - POPOVER_MARGIN);
  const spaceBelow = viewportH - anchorRect.bottom;
  const placeAbove = spaceBelow < POPOVER_MIN_HEIGHT && anchorRect.top > spaceBelow;
  let t3;
  if ($[2] !== anchorRect.bottom || $[3] !== anchorRect.top || $[4] !== left || $[5] !== placeAbove) {
    t3 = placeAbove ? {
      bottom: viewportH - anchorRect.top + POPOVER_MARGIN,
      left
    } : {
      top: anchorRect.bottom + POPOVER_MARGIN,
      left
    };
    $[2] = anchorRect.bottom;
    $[3] = anchorRect.top;
    $[4] = left;
    $[5] = placeAbove;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const style = t3;
  const portalTarget = document.fullscreenElement ?? document.body;
  let t4;
  if ($[7] !== style) {
    t4 = {
      ...style,
      position: "fixed",
      width,
      maxHeight: POPOVER_MAX_HEIGHT,
      zIndex: 400
    };
    $[7] = style;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onEnter) {
    t5 = e => {
      if (e.pointerType !== "mouse") {
        return;
      }
      onEnter();
    };
    $[9] = onEnter;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== onLeave) {
    t6 = e_0 => {
      if (e_0.pointerType !== "mouse") {
        return;
      }
      onLeave();
    };
    $[11] = onLeave;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== children || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = createPortal(<div data-post-factiiis-popover="true" style={t4} onPointerEnter={t5} onPointerLeave={t6} onClick={_temp} className="overflow-y-auto overflow-x-hidden rounded-lg border border-ds-border-standard bg-background-light p-2 shadow-xl">{children}</div>, portalTarget);
    $[13] = children;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp(e_1) {
  return e_1.stopPropagation();
}
type Pin = {
  postId: string;
  latitude: number;
  longitude: number;
  display: string | null;
  factiiiId: number;
};
function PinsPanel(t0) {
  const $ = _c(15);
  const {
    postId,
    pins,
    factiiisById,
    focusedPin,
    onSelectPin
  } = t0;
  let t1;
  if ($[0] !== factiiisById || $[1] !== focusedPin?.latitude || $[2] !== focusedPin?.longitude || $[3] !== onSelectPin || $[4] !== pins || $[5] !== postId) {
    let t2;
    if ($[7] !== factiiisById || $[8] !== focusedPin?.latitude || $[9] !== focusedPin?.longitude || $[10] !== onSelectPin || $[11] !== postId) {
      t2 = (p, i) => {
        const href = `/map?${mapLinkHref.parse({
          startPosition: {
            postId,
            latitude: p.latitude,
            longitude: p.longitude
          },
          pins: []
        }).href}`;
        const label = p.display?.trim() || `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`;
        const isFocused = focusedPin?.latitude === p.latitude && focusedPin?.longitude === p.longitude;
        return <li key={`${p.latitude},${p.longitude},${i}`}><Link href={href} onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onSelectPin(p);
          }} title={label} className={`flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm text-textPrimary hover:bg-primary/10 ${isFocused ? "bg-primary/10" : ""}`}><span className="flex shrink-0 items-end justify-center"><Avatar pin={true} factiiiId={p.factiiiId} factiiiAvatarKey={factiiisById.get(p.factiiiId)?.avatarKey ?? null} size={18} /></span><span className="min-w-0 flex-1 truncate">{label}</span></Link></li>;
      };
      $[7] = factiiisById;
      $[8] = focusedPin?.latitude;
      $[9] = focusedPin?.longitude;
      $[10] = onSelectPin;
      $[11] = postId;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = pins.map(t2);
    $[0] = factiiisById;
    $[1] = focusedPin?.latitude;
    $[2] = focusedPin?.longitude;
    $[3] = onSelectPin;
    $[4] = pins;
    $[5] = postId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <ul className="flex flex-col gap-1">{t1}</ul>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
type Tagger = Post['postFactiiisCount'][number]['postFactiiis'][number]['user'];
function PeoplePanel(t0) {
  const $ = _c(4);
  const {
    taggers
  } = t0;
  let t1;
  if ($[0] !== taggers) {
    t1 = taggers.map(_temp3);
    $[0] = taggers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="flex flex-col gap-1">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// Lists the retired factiiis behind the "Archive" chip — same row UX as the
// People/Pins panels: avatar + name, linking to the factiii.
function _temp3(u) {
  return <li key={u.id}><Link href={`/user/${u.username}`} onClick={_temp2} className="flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm text-textPrimary hover:bg-primary/10"><Avatar avatarKey={u.avatarKey} robohash={u.robohash} isBot={u.tag === "BOT"} size={24} userId={u.id} /><span className="min-w-0 flex-1 truncate">{u.name || `@${u.username}`}</span></Link></li>;
}
function _temp2(e) {
  return e.stopPropagation();
}
function RetiredPanel(t0) {
  const $ = _c(4);
  const {
    factiiis
  } = t0;
  let t1;
  if ($[0] !== factiiis) {
    t1 = factiiis.map(_temp5);
    $[0] = factiiis;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="flex flex-col gap-1">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// Lists the citations behind the "BookOpen" chip — the source factiiis this
// post's factiiis cite, plus any referenced posts. Same row UX as the other
// panels.
function _temp5(f) {
  return <li key={f.id}><Link href={`/f/${f.slug}`} onClick={_temp4} className="flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm text-textPrimary hover:bg-primary/10"><Avatar factiiiAvatarKey={f.avatarKey} factiiiId={f.id} size={24} isRetired={true} /><span className="min-w-0 flex-1 truncate">{f.name}</span></Link></li>;
}
function _temp4(e) {
  return e.stopPropagation();
}
type SourceFactiii = NonNullable<Post['postFactiiisCount'][number]['postFactiiis'][number]['factiiiReferences']>[number]['factiii'];
function ReferencesPanel(t0) {
  const $ = _c(8);
  const {
    refPosts,
    sources,
    onViewPosts
  } = t0;
  let t1;
  if ($[0] !== sources) {
    t1 = sources.length > 0 && <div className="flex flex-col gap-1"><span className="px-2 text-xs font-emphasis text-textSecondary/70">Sources</span><ul className="flex flex-col gap-1">{sources.map(_temp7)}</ul></div>;
    $[0] = sources;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onViewPosts || $[3] !== refPosts.length) {
    t2 = refPosts.length > 0 && <div className="flex flex-col gap-1"><span className="px-2 text-xs font-emphasis text-textSecondary/70">Referenced posts</span><button type="button" onClick={e_0 => {
        e_0.stopPropagation();
        onViewPosts();
      }} className="flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm text-textPrimary hover:bg-primary/10"><BookOpen size={16} className="shrink-0 text-primary" /><span className="min-w-0 flex-1 truncate text-left">View {refPosts.length} referenced post{refPosts.length === 1 ? "" : "s"}</span></button></div>;
    $[2] = onViewPosts;
    $[3] = refPosts.length;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-2">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp7(f) {
  return <li key={`source-${f.id}`}><Link href={`/f/${f.slug}`} onClick={_temp6} className="flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm text-textPrimary hover:bg-primary/10"><Avatar factiiiId={f.id} factiiiAvatarKey={f.avatarKey} size={24} /><span className="min-w-0 flex-1 truncate">{f.name}</span></Link></li>;
}
function _temp6(e) {
  return e.stopPropagation();
}
function LinksPanel(t0) {
  const $ = _c(11);
  const {
    links,
    factiiisById,
    showHover,
    hideHover
  } = t0;
  let t1;
  if ($[0] !== factiiisById || $[1] !== hideHover || $[2] !== links || $[3] !== showHover) {
    let t2;
    if ($[5] !== factiiisById || $[6] !== hideHover || $[7] !== showHover) {
      t2 = t3 => {
        const {
          url,
          factiiiId
        } = t3;
        return <div key={url} className="flex min-w-0 items-center"><Link href={`/f/${factiiisById.get(factiiiId)?.slug ?? ""}`} onClick={_temp8} onMouseEnter={e_0 => showHover(factiiiId, e_0.currentTarget.getBoundingClientRect())} onMouseLeave={() => hideHover(factiiiId)} onFocus={e_1 => showHover(factiiiId, e_1.currentTarget.getBoundingClientRect())} onBlur={() => hideHover(factiiiId)} className="mr-2 shrink-0 rounded-full transition-transform hover:scale-110"><Avatar factiiiId={factiiiId} factiiiAvatarKey={factiiisById.get(factiiiId)?.avatarKey ?? null} size={24} /></Link><div className="min-w-0 flex-1"><LinkPreview url={url} compact={true} fallback={<a href={url} target="_blank" rel="noopener noreferrer" onClick={_temp9} title={url} className="flex min-w-0 items-start gap-2 text-sm text-primary hover:underline"><LinkIcon size={14} className="mt-0.5 shrink-0" /><span className="min-w-0 flex-1 break-all">{url}</span></a>} /></div></div>;
      };
      $[5] = factiiisById;
      $[6] = hideHover;
      $[7] = showHover;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = links.map(t2);
    $[0] = factiiisById;
    $[1] = hideHover;
    $[2] = links;
    $[3] = showHover;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <div className="flex min-w-0 flex-col gap-2">{t1}</div>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function _temp9(e_2) {
  return e_2.stopPropagation();
}
function _temp8(e) {
  return e.stopPropagation();
}